import React, { useEffect, useState } from 'react';
import { t } from './i18n';

// Floating "Install app" prompt for the PWA.
//  - Android/desktop Chrome: captures the beforeinstallprompt event and shows a
//    button that triggers the native install dialog.
//  - iOS Safari (no beforeinstallprompt): shows a one-time hint to use
//    Share → "Add to Home Screen".
//  - Hidden entirely when already installed (running standalone) or once the
//    user dismisses it (remembered in localStorage, so we don't nag).
const DISMISS_KEY = 'jc_pwa_dismissed';

function isStandalone() {
  return (
    window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
  ) || window.navigator.standalone === true;
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) {}
    if (dismissed) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    const onInstalled = () => {
      setShow(false);
      setIosHint(false);
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch (er) {}
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS never fires beforeinstallprompt — show the manual hint instead.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setIosHint(true), 2500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) {}
    setDeferred(null);
    setShow(false);
  };

  if (!show && !iosHint) return null;

  const wrap = {
    position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 16,
    zIndex: 4000, maxWidth: 'min(440px, calc(100vw - 24px))', width: 'max-content',
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#fff', color: '#1f2937', border: '1px solid #e5e7eb',
    borderRadius: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.18)', padding: '12px 14px',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  };
  const iconBox = {
    width: 40, height: 40, borderRadius: 10, background: '#1a4d8f', flex: '0 0 auto',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const btn = {
    background: '#1a4d8f', color: '#fff', border: 'none', borderRadius: 9,
    padding: '9px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
  };
  const closeBtn = {
    background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 20,
    lineHeight: 1, cursor: 'pointer', padding: '0 2px', flex: '0 0 auto',
  };

  return (
    <div style={wrap} role="dialog" aria-label={t('Install Jewish Compass')}>
      <div style={iconBox}>
        <img src="/favicon-256.png" alt="" width="26" height="26" style={{ display: 'block' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{t('Install Jewish Compass')}</div>
        <div style={{ fontSize: 12.5, color: '#6b7280' }}>
          {iosHint
            ? t('Tap the Share button, then “Add to Home Screen”.')
            : t('Add it to your home screen for quick access.')}
        </div>
      </div>
      {show && !iosHint && <button style={btn} onClick={install}>{t('Install')}</button>}
      <button style={closeBtn} onClick={dismiss} aria-label={t('Dismiss')}>×</button>
    </div>
  );
}
